"use client";

// Visual for the "Receive financing within hours" bento card (design handoff:
// drawdown-to-bank-animation.md): a funds tile with a "Drawdown" button pressed
// by a cursor, a connector, and a bank tile whose balance counts up. A fixed
// 560x230 stage on a 9s rAF loop, scaled down to fit narrower cards. Below
// 536px the tiles stack (320x380 stage, vertical connector) so they stay legible.

import * as React from "react";

import { cn } from "@/lib/utils";

import {
  DRAWDOWN_AMOUNT,
  DRAWDOWN_REST_SECONDS,
  getDrawdownFrame,
} from "./drawdown-timeline";
import { useLoopSeconds } from "./use-stage-loop";

interface DrawdownVisualProps {
  availableFundsLabel: string;
  limitLabel: string;
  availableLabel: string;
  drawdownLabel: string;
  bankAccountLabel: string;
  availableBalanceLabel: string;
  receivedLabel: string;
  className?: string;
}

const STAGE_WIDTH = 560;
const STAGE_STACKED_WIDTH = 320;
// Matches the `min-[536px]:` classes below.
const STACK_BREAKPOINT = "(min-width: 536px)";
const TILE_SHADOW = "shadow-[0_2px_20px_rgba(22,21,23,0.08)]";

const whole = new Intl.NumberFormat("en-US");
const cents = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export function DrawdownVisual({
  availableFundsLabel,
  limitLabel,
  availableLabel,
  drawdownLabel,
  bankAccountLabel,
  availableBalanceLabel,
  receivedLabel,
  className,
}: DrawdownVisualProps) {
  const root = React.useRef<HTMLDivElement>(null);
  const seconds = useLoopSeconds(root, DRAWDOWN_REST_SECONDS);
  const [scale, setScale] = React.useState(1);

  React.useEffect(() => {
    const el = root.current;
    if (!el) return;
    const wide = window.matchMedia(STACK_BREAKPOINT);
    const fit = () =>
      setScale(
        Math.min(
          1,
          el.clientWidth / (wide.matches ? STAGE_WIDTH : STAGE_STACKED_WIDTH)
        )
      );
    fit();
    const resize = new ResizeObserver(fit);
    resize.observe(el);
    wide.addEventListener("change", fit);
    return () => {
      resize.disconnect();
      wide.removeEventListener("change", fit);
    };
  }, []);

  const f = getDrawdownFrame(seconds);

  return (
    <div
      ref={root}
      aria-hidden
      className={cn(
        "relative mt-auto h-[calc(var(--s)*380px)] min-[536px]:absolute min-[536px]:inset-x-6 min-[536px]:mt-0 min-[536px]:h-[calc(var(--s)*230px)] md:inset-x-10",
        className
      )}
      style={{ "--s": scale } as React.CSSProperties}
    >
      <div className="relative mx-auto flex h-[380px] w-[320px] origin-top-left scale-[var(--s)] flex-col items-center gap-10 min-[536px]:h-[230px] min-[536px]:w-[560px] min-[536px]:flex-row min-[536px]:justify-between min-[536px]:gap-0">
        <div
          className={cn(
            "border-grey-100 bg-grey-0 relative box-border flex h-[190px] w-[320px] shrink-0 items-center justify-between gap-4 rounded-[16px] border px-[22px] py-5",
            TILE_SHADOW
          )}
        >
          <div className="flex min-w-0 flex-col gap-1">
            <span className="text-grey-950 text-[15px] leading-[1.3] font-medium tracking-[-0.02em]">
              {availableFundsLabel}
            </span>
            <div className="flex flex-col gap-0.5 whitespace-nowrap tabular-nums">
              <span className="text-grey-950 text-[22px] leading-[1.2] font-medium tracking-[-0.03em]">
                <span className="text-grey-500">$</span>
                {whole.format(f.available)}
              </span>
              <span className="text-grey-500 text-[12px] leading-[1.2] tracking-[-0.02em]">
                {limitLabel}
              </span>
            </div>
            <div className="relative mt-3 self-start">
              <div
                className={cn(
                  "text-grey-0 rounded-full px-[18px] py-2 text-[13px] leading-[1.4] font-medium whitespace-nowrap transition-[background,transform] duration-150",
                  f.pressed ? "scale-[0.93] bg-[#0016c4]" : "bg-pulse-700"
                )}
              >
                {drawdownLabel}
              </div>
              <svg
                width="22"
                height="26"
                viewBox="0 0 22 26"
                className="pointer-events-none absolute top-[55%] left-1/2 drop-shadow-md"
                style={{
                  opacity: f.cursor.opacity,
                  transform: `translate(${f.cursor.x}px, ${f.cursor.y}px)`,
                }}
              >
                <path
                  d="M2 1.5v19l5.2-4.6 3.4 7.6 3.2-1.4-3.4-7.5H18z"
                  fill="#0c0d0d"
                  stroke="#ffffff"
                  strokeWidth="1.6"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          </div>

          <div className="relative size-[136px] shrink-0">
            <svg
              viewBox="0 0 136 136"
              className="absolute inset-0 -rotate-90"
              fill="none"
            >
              <circle
                cx="68"
                cy="68"
                r="60"
                stroke="#d9d9d9"
                strokeWidth="9"
                strokeLinecap="round"
                pathLength={1}
                strokeDasharray={f.ring.trackDash}
                strokeDashoffset={f.ring.trackOffset}
              />
              <circle
                cx="68"
                cy="68"
                r="60"
                stroke="#5ccf7a"
                strokeWidth="9"
                strokeLinecap="round"
                pathLength={1}
                strokeDasharray={f.ring.fillDash}
                strokeDashoffset={0}
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-px">
              <span className="text-grey-950 text-[26px] leading-[1.1] font-medium tracking-[-0.03em] tabular-nums">
                {f.percent}%
              </span>
              <span className="text-grey-500 text-[12px]">
                {availableLabel}
              </span>
            </div>
          </div>
        </div>

        <svg
          width="40"
          height="40"
          viewBox="0 0 40 40"
          className="absolute top-[190px] left-[140px] overflow-visible min-[536px]:hidden"
          fill="none"
        >
          <path
            d="M20 0V40"
            stroke="#b0b3b5"
            strokeWidth="1.5"
            pathLength={1}
            strokeDasharray="0.12 0.09"
            opacity={0.35}
          />
          <path
            d="M20 0V40"
            stroke="#001eff"
            strokeWidth="1.5"
            strokeLinecap="round"
            pathLength={1}
            strokeDasharray={`${f.line} 1`}
          />
          <circle
            cx="20"
            cy={40 * f.dotK}
            r="3"
            fill="#001eff"
            opacity={f.dotVisible ? 1 : 0}
          />
        </svg>

        <svg
          width="40"
          height="40"
          viewBox="0 0 40 40"
          className="absolute top-[95px] left-[320px] hidden overflow-visible min-[536px]:block"
          fill="none"
        >
          <path
            d="M0 20H40"
            stroke="#b0b3b5"
            strokeWidth="1.5"
            pathLength={1}
            strokeDasharray="0.12 0.09"
            opacity={0.35}
          />
          <path
            d="M0 20H40"
            stroke="#001eff"
            strokeWidth="1.5"
            strokeLinecap="round"
            pathLength={1}
            strokeDasharray={`${f.line} 1`}
          />
          <circle
            cx={40 * f.dotK}
            cy="20"
            r="3"
            fill="#001eff"
            opacity={f.dotVisible ? 1 : 0}
          />
        </svg>

        <div
          className={cn(
            "relative box-border flex h-[150px] w-[320px] shrink-0 flex-col justify-between rounded-[16px] px-5 py-[18px] text-white min-[536px]:w-[200px]",
            TILE_SHADOW
          )}
          style={{
            background:
              "linear-gradient(180deg, #001eff 0%, #0a14b8 55%, #000633 100%)",
          }}
        >
          <div className="flex items-center justify-between">
            <span className="flex items-center gap-2 text-[13px] font-medium">
              <span className="flex size-[22px] items-center justify-center rounded-full bg-[#e0e6ff] text-[11px] font-semibold text-[#001eff]">
                W
              </span>
              {bankAccountLabel}
            </span>
            <span className="text-[11px] text-[#b9c3ff] tabular-nums">
              ·· 8323
            </span>
          </div>
          <div>
            <div className="mb-1 text-[11px] text-[#b9c3ff]">
              {availableBalanceLabel}
            </div>
            <div className="text-[24px] leading-[1.1] font-medium tracking-[-0.03em] whitespace-nowrap tabular-nums">
              <span className="text-[#b9c3ff]">$</span>
              {cents.format(f.balance)}
            </div>
          </div>
          <div
            className="flex min-h-4 items-center gap-1.5 text-[11px] tabular-nums transition-opacity duration-[250ms]"
            style={{ opacity: f.receivedVisible ? 1 : 0 }}
          >
            <svg
              width="12"
              height="12"
              viewBox="0 0 12 12"
              fill="none"
              stroke="#5ccf7a"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M9 3L3 9M4 3h5v5" />
            </svg>
            <span className="text-[#5ccf7a]">
              +${cents.format(DRAWDOWN_AMOUNT)}
            </span>
            <span className="text-[#b9c3ff]">{receivedLabel}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
